/**
 * @module api/config
 *
 * Central Axios configuration for all API requests. Sets up:
 * - A shared Axios instance with base URL and default headers
 * - A request interceptor that attaches JWT Bearer tokens
 * - A response interceptor that automatically refreshes expired
 *   access tokens using the stored refresh token, queuing any
 *   concurrent requests that arrive while a refresh is in progress
 * - Redirect-to-login logic with a circuit breaker to prevent loops
 */

import axios from "axios";
import { useLogin } from "@/composables/useLogin";

/**
 * Pre-configured Axios instance used by all API modules.
 * Base URL comes from the VITE_API_BASE_URL environment variable.
 * All requests have a 30-second timeout and send JSON content type.
 */
export const api = axios.create({
  timeout: 30000, // 30 seconds timeout for all requests
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Flag to prevent multiple simultaneous token refresh attempts.
 * When true, subsequent 401 responses will queue their requests
 * instead of triggering additional refresh calls.
 */
let isRefreshing = false;

/**
 * Queue of pending requests waiting for a token refresh to complete.
 * Each entry has { resolve, reject } callbacks from a Promise that
 * the queued request is awaiting.
 */
let failedQueue = [];

/**
 * Flag to suppress error handling once we have decided to redirect
 * to the login page. Prevents cascading error notifications during
 * the redirect.
 */
let isRedirectingToLogin = false;

/**
 * Process all queued requests after a token refresh attempt completes.
 * If the refresh succeeded, each queued request is resolved with the
 * new token so it can retry. If it failed, each is rejected with the error.
 * If we are redirecting to login, the queue is silently cleared to
 * avoid triggering error handlers in calling code.
 *
 * @param {Error|null} error - The refresh error, or null on success
 * @param {string|null} token - The new access token on success
 */
const processQueue = (error, token = null) => {
  // If redirecting to login, don't reject - just clear the queue silently
  if (isRedirectingToLogin) {
    failedQueue = [];
    return;
  }

  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Request interceptor: attaches the JWT access token from localStorage
 * as a Bearer Authorization header on every outgoing request.
 */
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");

    // Attach the Bearer token if one exists in local storage
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor: handles 401 Unauthorized responses by attempting
 * to refresh the access token. If a refresh is already in progress,
 * the failed request is queued and retried once the refresh completes.
 *
 * Auth-related endpoints (containing "/auth/") are excluded from
 * refresh attempts to avoid infinite loops (e.g., the refresh endpoint
 * itself returning 401).
 *
 * The _retry flag on the original request prevents infinite retry loops
 * for a single request.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401 errors on non-auth endpoints that haven't been retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/")
    ) {
      if (isRefreshing) {
        // Another refresh is already in progress - queue this request
        // and wait for the refresh to complete before retrying
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        // No refresh token available - user must log in again
        return redirectToLogin("No refresh token available");
      }

      try {
        // Call the refresh endpoint with a plain axios instance (not the
        // intercepted `api`) to avoid triggering this interceptor again
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken, user } = response.data;

        // Persist the new tokens and user data to localStorage
        localStorage.setItem("accessToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        localStorage.setItem("user", JSON.stringify(user));

        // Update the original failed request with the new token
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        // Resolve all queued requests with the new token so they can retry
        processQueue(null, newAccessToken);

        // Retry the original failed request with the new token
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh token is also invalid/expired - session is over
        return redirectToLogin("Session expired");
      } finally {
        isRefreshing = false;
      }
    }

    // If we are already in the process of redirecting to login,
    // return a never-resolving promise to suppress error handling
    // in the calling code (the page will navigate away shortly)
    if (isRedirectingToLogin) {
      return new Promise(() => {});
    }

    return Promise.reject(error);
  },
);

/**
 * Clear all authentication data from both localStorage and reactive
 * Vue state by delegating to the useLogin composable's logout function.
 */
function clearAuth() {
  const { logout } = useLogin();
  logout(); // Clears both localStorage and reactive refs
}

/**
 * Redirect the user to the login page silently, without triggering
 * error handlers in the calling API code.
 *
 * Includes a circuit breaker (tracked in sessionStorage) to prevent
 * infinite redirect loops if something is misconfigured. After 3
 * consecutive redirects, it stops and logs an error.
 *
 * Returns a never-resolving Promise so the original API call's
 * .then/.catch chain never executes -- the page navigates away instead.
 *
 * @param {string} reason - Human-readable reason for the redirect (logged to console)
 * @returns {Promise} A Promise that never resolves or rejects
 */
function redirectToLogin(reason) {
  // Circuit breaker: prevent redirect loops by counting consecutive redirects
  const redirectCount = parseInt(sessionStorage.getItem("authRedirectCount") || "0", 10);
  if (redirectCount > 2) {
    console.error("Auth redirect loop detected, stopping. Clear storage and refresh.");
    sessionStorage.removeItem("authRedirectCount");
    return new Promise(() => {});
  }

  // If we are already redirecting, just return a pending promise
  if (isRedirectingToLogin) {
    return new Promise(() => {});
  }

  isRedirectingToLogin = true;
  sessionStorage.setItem("authRedirectCount", String(redirectCount + 1));

  // Clear any queued requests silently so they don't fire error handlers
  processQueue(new Error("Auth expired"), null);

  // Clear auth data (tokens, user info) from storage and reactive state
  clearAuth();

  // Log for debugging purposes
  console.warn(`Redirecting to login: ${reason} (count: ${redirectCount + 1})`);

  // Navigate to login using replace to prevent the back button from
  // returning to the expired-session page
  window.location.replace("/login");

  // Return a never-resolving promise to prevent error handling
  // in the calling code -- the page will redirect before this matters
  return new Promise(() => {});
}
